import { ReservationRate } from '~/services/ReservationService';
import { generateDateRange } from '~/services/TimeService';

/**
 * Generate minimal synthetic ReservationRate array for LPP (bedbank) items.
 * - Evenly distributes room sell and cost totals across nights, with last-night adjustment
 */
export default function getLPPSyntheticReservationRates(
  item: any,
  room: any,
  orderCurrencyCode: string,
): Array<ReservationRate> {
  const checkIn = new Date(item.check_in);
  const checkOut = new Date(item.check_out);
  const dates = generateDateRange(checkIn, checkOut);

  const nights = Math.max(1, Number(item.nights || dates.length || 1));

  const totalSell = Number(room?.price ?? item?.total ?? 0) || 0;
  const totalCost = Number(room?.cost_price ?? 0) || 0;

  const baseSellPerNight = nights > 0 ? +(totalSell / nights).toFixed(2) : 0;
  const baseCostPerNight = nights > 0 ? +(totalCost / nights).toFixed(2) : 0;

  // Adjust the last night to fix rounding so sums match totals
  const sellByNight: Array<number> = new Array(dates.length).fill(baseSellPerNight);
  const costByNight: Array<number> = new Array(dates.length).fill(baseCostPerNight);
  const sellSoFar = +(baseSellPerNight * (nights - 1)).toFixed(2);
  const costSoFar = +(baseCostPerNight * (nights - 1)).toFixed(2);
  if (dates.length > 0) {
    sellByNight[dates.length - 1] = +(totalSell - sellSoFar).toFixed(2);
    costByNight[dates.length - 1] = +(totalCost - costSoFar).toFixed(2);
  }

  const reservationId = item?.id_reservation_room || item?.id;
  const nowIso = new Date().toISOString();

  const costCurrencyCode = room?.supplier_currency_code || orderCurrencyCode;

  return dates.map((d, idx) => {
    const dateIso = new Date(d).toISOString();
    const sell_amount = sellByNight[idx] || 0;
    const cost_amount = costByNight[idx] || 0;

    const rate: ReservationRate = {
      id: `${reservationId}-${idx}`,
      hotel_reservation_id: reservationId,
      date: dateIso,
      sell_currency: orderCurrencyCode,
      sell_amount,
      cost_currency: costCurrencyCode,
      cost_amount,
      cost_amount_for_cm: cost_amount,
      original_cost_amount: cost_amount,
      original_cost_amount_for_cm: cost_amount,
      extra_guest_surcharge_sell_amount: 0,
      extra_guest_surcharge_cost_amount: 0,
      peak_period_surcharge_sell_amount: 0,
      peak_period_surcharge_cost_amount: 0,
      surcharge_internal_sell_amount: 0,
      createdAt: nowIso,
      updatedAt: nowIso,
    };
    return rate;
  });
}
